import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Button, Select } from '@artosera/ui';
import { usePeut } from '../../auth/usePeut.js';
import { listerOuvertures } from '../../lib/api.js';
import { ETATS_OUVERTURE_TOUS, formaterJour, joursDeRetard } from '../../lib/ouvertures.js';
import type { EtatOuverture, ResumeOuverture } from '../../types.js';
import { TableauDefilant } from '../../components/TableauDefilant.js';
import { BadgeTest } from '../EspaceDeTest.js';
import pageStyles from '../../styles/page.module.css';
import tableStyles from '../../styles/table.module.css';
import { BadgeEtatOuverture } from './commun.js';
import styles from './Ouvertures.module.css';

const TAILLE_PAGE = 50;
type FiltreResponsable = 'tous' | 'moi';

function cleProchaineAction(ligne: ResumeOuverture): string {
  if (ligne.etat === 'brouillon' && ligne.estTest) return 'brouillonTest';
  return ligne.etat;
}

/**
 * `/ouvertures` : les dossiers de mise en service (ARTO-128). L'état, le
 * numéro et le responsable sont ceux de l'API ; le retard seul se déduit de
 * la date de bascule prévue.
 */
export function OuverturesList(): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const navigate = useNavigate();
  const [etat, setEtat] = useState<EtatOuverture | 'tous'>('tous');
  const [responsable, setResponsable] = useState<FiltreResponsable>('tous');
  const [page, setPage] = useState(1);

  const requete = useQuery({
    queryKey: ['console', 'ouvertures', etat, responsable, page],
    queryFn: () =>
      listerOuvertures({
        ...(etat === 'tous' ? {} : { etat }),
        ...(responsable === 'moi' ? { responsable: 'moi' } : {}),
        page,
      }),
  });

  const lignes = requete.data?.items ?? [];
  const total = requete.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / TAILLE_PAGE));
  const filtre = etat !== 'tous' || responsable !== 'tous';

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('ouvertures.titre')}</h1>
          <p>{t('ouvertures.texte')}</p>
        </div>
        {peut('console:manage-onboarding') ? (
          <Button onClick={() => navigate('/ouvertures/nouvelle')}>
            {t('ouvertures.nouvelle')}
          </Button>
        ) : null}
      </div>

      <div className={tableStyles.toolbar}>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="ouvertures-filtre-etat">{t('ouvertures.filtres.etat')}</label>
          <Select
            id="ouvertures-filtre-etat"
            value={etat}
            onValueChange={(valeur) => {
              setEtat(valeur as EtatOuverture | 'tous');
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('ouvertures.filtres.tousEtats') },
              ...ETATS_OUVERTURE_TOUS.map((valeur) => ({
                value: valeur,
                label: t(`ouvertures.etat.${valeur}`),
              })),
            ]}
          />
        </div>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="ouvertures-filtre-responsable">
            {t('ouvertures.filtres.responsable')}
          </label>
          <Select
            id="ouvertures-filtre-responsable"
            value={responsable}
            onValueChange={(valeur) => {
              setResponsable(valeur as FiltreResponsable);
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('ouvertures.filtres.tous') },
              { value: 'moi', label: t('ouvertures.filtres.moi') },
            ]}
          />
        </div>
      </div>

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('ouvertures.chargement')}</p>
      ) : requete.isError ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('ouvertures.erreurChargement')}
        </p>
      ) : lignes.length === 0 ? (
        <p className={pageStyles.videEtat}>
          {filtre ? t('ouvertures.vide') : t('ouvertures.videTout')}
        </p>
      ) : (
        <>
          <TableauDefilant libelle={t('ouvertures.titre')}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('ouvertures.colonnes.numero')}</th>
                  <th scope="col">{t('ouvertures.colonnes.nom')}</th>
                  <th scope="col">{t('ouvertures.colonnes.etat')}</th>
                  <th scope="col">{t('ouvertures.colonnes.responsable')}</th>
                  <th scope="col">{t('ouvertures.colonnes.bascule')}</th>
                  <th scope="col">{t('ouvertures.colonnes.prochaine')}</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => {
                  const retard = joursDeRetard(ligne.basculePrevueLe, ligne.etat);
                  return (
                    <tr key={ligne.id}>
                      <td>
                        <span className={styles.numero}>{ligne.numero}</span>
                      </td>
                      <td className={styles.cellulePrincipale}>
                        <Link to={`/ouvertures/${ligne.id}`}>{ligne.nomCommercial}</Link>
                        {ligne.estTest ? (
                          <>
                            {' '}
                            <BadgeTest />
                          </>
                        ) : null}
                        <div className={tableStyles.celluleMuette}>{ligne.slug}</div>
                      </td>
                      <td>
                        <BadgeEtatOuverture etat={ligne.etat} />
                      </td>
                      <td>
                        {ligne.responsable ?? (
                          <span className={tableStyles.celluleMuette}>
                            {t('ouvertures.sansResponsable')}
                          </span>
                        )}
                      </td>
                      <td>
                        {ligne.basculePrevueLe ? (
                          formaterJour(ligne.basculePrevueLe)
                        ) : (
                          <span className={tableStyles.celluleMuette}>
                            {t('ouvertures.sansDate')}
                          </span>
                        )}
                        {retard > 0 ? (
                          <span className={styles.retard}>
                            {t('ouvertures.retard', { count: retard })}
                          </span>
                        ) : null}
                      </td>
                      <td>{t(`ouvertures.prochaineAction.${cleProchaineAction(ligne)}`)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableauDefilant>
          {pages > 1 ? (
            <div className={tableStyles.pagination}>
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((courante) => Math.max(1, courante - 1))}
              >
                {t('ouvertures.pagination.precedente')}
              </Button>
              <span>{t('ouvertures.pagination.page', { page, total: pages })}</span>
              <Button
                variant="secondary"
                disabled={page >= pages}
                onClick={() => setPage((courante) => courante + 1)}
              >
                {t('ouvertures.pagination.suivante')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
